import Table from 'core/components/data/table/Table'
import { TableColumnType } from 'core/components/data/table/TableTypes'
import { useMemo } from 'react'
import { TaxReport } from 'types/entity'
import { tableCellFormat } from 'utils/formatter'
import MonthlyBreakdownDrawer from './MonthlyBreakdownDrawer'

interface Props {
  taxData?: TaxReport
  status: 'idle' | 'loading' | 'error' | 'success'
}

type RowData = {
  approved_mileage: string
  payments_received: string
  approved_mileage_while_non_compliant: number
  payments_received_while_non_compliant: number
  excluded_from_tax: number
  subject_to_tax: number
  quarter: string
}

const TaxReportTable = ({ taxData, status }: Props) => {
  const tableData = useMemo((): RowData[] => {
    if (taxData) {
      const data = Object.entries(taxData.by_quarter).map(([key, value]) => ({
        quarter: key,
        ...value
      }))
      return data
    }
    return []
  }, [taxData])

  const quarterlyData = useMemo((): {
    [key: string]: TaxReport['by_month'][string][]
  } | null => {
    if (taxData) {
      return {
        '1': [
          taxData.by_month['1'],
          taxData.by_month['2'],
          taxData.by_month['3']
        ],
        '2': [
          taxData.by_month['4'],
          taxData.by_month['5'],
          taxData.by_month['6']
        ],
        '3': [
          taxData.by_month['7'],
          taxData.by_month['8'],
          taxData.by_month['9']
        ],
        '4': [
          taxData.by_month['10'],
          taxData.by_month['11'],
          taxData.by_month['12']
        ]
      }
    }
    return null
  }, [taxData])

  const columns = useMemo((): TableColumnType<RowData> => {
    return [
      {
        title: 'Date',
        key: 'quarter',
        className: 'min-w-[152px]',
        render: (row: RowData) => {
          return `Quarter ${row.quarter}, ${taxData?.summary.year}`
        }
      },
      {
        title: 'Approved mileage while non-compliant',
        key: 'approved_mileage_while_non_compliant',
        className: 'min-w-[190px]',
        align: 'right',
        render: (row: RowData) =>
          tableCellFormat(row.approved_mileage_while_non_compliant, 'miles')
      },
      {
        title: 'Payments received while non-compliant',
        key: 'payments_received_while_non_compliant',
        className: 'min-w-[190px]',
        align: 'right',
        render: (row: RowData) =>
          tableCellFormat(row.payments_received_while_non_compliant, 'dollars')
      },
      {
        title: 'Excluded from tax',
        key: 'excluded_from_tax',
        className: 'min-w-[190px]',
        align: 'right',
        render: (row: RowData) =>
          tableCellFormat(row.excluded_from_tax, 'dollars')
      },
      {
        title: 'Subject to tax',
        key: 'subject_to_tax',
        className: 'min-w-[190px]',
        align: 'right',
        render: (row: RowData) => tableCellFormat(row.subject_to_tax, 'dollars')
      },
      {
        title: '',
        key: 'actions',
        align: 'center',
        render: (row: RowData) => {
          if (
            !quarterlyData ||
            !taxData ||
            row.payments_received_while_non_compliant === 0
          )
            return null

          return (
            <MonthlyBreakdownDrawer
              year={taxData.summary.year}
              quarter={row.quarter}
              quarterlyData={quarterlyData[row.quarter]}
            />
          )
        }
      }
    ]
  }, [quarterlyData, taxData])

  return (
    <div className="card p-0">
      <Table<RowData>
        data={tableData}
        columns={columns}
        status={status}
        disableHover
        rowKey="quarter"
      />
    </div>
  )
}

export default TaxReportTable
